import { ResultOf } from 'gql.tada';
import { ClickThroughSourceLocalStorageValue } from '../../../../analytics/click-through-tracking/TrackingSource';
import { buttonFragment } from '../../../../graphql/buttonFragment';
import { textFragment } from '../../../../graphql/textFragment';
import { weblinkFragment } from '../../../../graphql/weblinkFragment';
import { isNotNullOrUndefinedValue } from '../../../../helpers/isNotNullOrUndefinedValue';
import { isNonEmptyArray } from '../../../helpers/arrayHelpers';
import { mapApiWeblinkIconInfoToLinkIconInfo } from '../../../helpers/mapApiWeblinkIconInfoToLinkIconInfo';
import { mapApiWeblinkVariantToLinkVariant } from '../../../helpers/mapApiWeblinkVariantToLinkVariant';
import { mapButtonVariant } from '../../../helpers/mapButtonVariant';
import { DynamicContentName, DynamicContentsItem, DynamicContentWebLink } from '../types';

export type ButtonFragment = ResultOf<typeof buttonFragment>;
export type WebLinkFragment = ResultOf<typeof weblinkFragment>;
export type TextFragment = ResultOf<typeof textFragment>;

type WebLinkFromAPIWithoutTypename = Omit<
  WebLinkFragment,
  '__typename' | 'iconInfo' | 'tracking'
> & {
  iconInfo?: Omit<NonNullable<WebLinkFragment['iconInfo']>, '__typename'> | null;
  tracking?:
    | (Omit<
        NonNullable<WebLinkFragment['tracking']>,
        '__typename' | 'outboundTracking' | 'clickThroughTracking'
      > & {
        outboundTracking?: Omit<
          Omit<
            NonNullable<NonNullable<WebLinkFragment['tracking']>['outboundTracking']>,
            'data'
          > & {
            data: Omit<
              NonNullable<
                NonNullable<NonNullable<WebLinkFragment['tracking']>['outboundTracking']>['data']
              >,
              '__typename'
            >;
          },
          '__typename'
        > | null;
        clickThroughTracking?: Omit<
          NonNullable<NonNullable<WebLinkFragment['tracking']>['clickThroughTracking']>,
          '__typename'
        > | null;
      })
    | null;
};

export interface ButtonFromAPIWithClickHandler extends ButtonFragment {
  onClick?: () => void;
}
const mapColorTokenToColor = (colorToken: string | null | undefined): string | null => {
  return colorToken ? `var(--ck-${colorToken})` : null;
};

export const mapWebLink = (
  webLink: WebLinkFromAPIWithoutTypename,
  clickThroughSource: ClickThroughSourceLocalStorageValue | undefined = undefined,
): DynamicContentWebLink => {
  let clickThroughTracking = null;

  if (clickThroughSource) {
    clickThroughTracking = clickThroughSource;
  } else if (webLink.tracking?.clickThroughTracking) {
    clickThroughTracking = {
      pathname: webLink.tracking.clickThroughTracking.pathname,
      page: webLink.tracking.clickThroughTracking.page,
      element: webLink.tracking.clickThroughTracking.element,
      sourceType: webLink.tracking.clickThroughTracking.sourceType,
    };
  }

  return {
    __typename: 'WebLink',
    title: webLink.title,
    url: webLink.url,
    isExternal: webLink.isExternal,
    variant: mapApiWeblinkVariantToLinkVariant(webLink.variant),
    iconInfo: webLink.iconInfo ? mapApiWeblinkIconInfoToLinkIconInfo(webLink.iconInfo) : null,
    name: DynamicContentName.WebLink,
    ...(webLink.rel && { rel: webLink.rel }),
    tracking: {
      outboundTracking: webLink.tracking?.outboundTracking
        ? {
            schema: webLink.tracking.outboundTracking.schema,
            data: {
              event_name: webLink.tracking.outboundTracking.data.event_name,
              type: webLink.tracking.outboundTracking.data.type,
              click_url: webLink.tracking.outboundTracking.data.click_url,
            },
          }
        : null,
      clickThroughTracking,
      ...(typeof webLink.disabled === 'boolean' && { disabled: webLink.disabled }),
    },
  };
};

export const mapDynamicContentItem = (
  dynamicContentItem:
    | ButtonFragment
    | ButtonFromAPIWithClickHandler
    | TextFragment
    | WebLinkFragment
    | null
    | undefined,
): DynamicContentsItem | null => {
  if (!dynamicContentItem) return null;

  switch (dynamicContentItem.__typename) {
    case 'Button':
      return {
        ...dynamicContentItem,
        __typename: dynamicContentItem.__typename,
        name: DynamicContentName.Button,
        buttonVariant: mapButtonVariant(dynamicContentItem.buttonVariant),
      };
    case 'WebLink':
      return mapWebLink(dynamicContentItem);
    case 'Text': {
      const { colorToken, __typename, ...rest } = dynamicContentItem;
      return {
        ...rest,
        color: mapColorTokenToColor(colorToken),
        __typename,
        name: DynamicContentName.Text,
      };
    }
    default:
      return null;
  }
};

export const mapDynamicContentItems = (
  dynamicContentItems?:
    | (ButtonFragment | TextFragment | WebLinkFragment | null | undefined)[]
    | null,
): DynamicContentsItem[] => {
  if (!isNonEmptyArray(dynamicContentItems)) return [];
  return dynamicContentItems
    .map(mapDynamicContentItem)
    .filter(Boolean)
    .filter(isNotNullOrUndefinedValue);
};
